import type { CSSProperties } from 'react';

interface HoverRevealMotionTokens {
    motionDurationFast: string;
    motionEaseInOut: string;
}

/** Keeps hover-only controls mounted so their appearance never shifts surrounding content. */
export const getHoverRevealStyle = (
    visible: boolean,
    token: HoverRevealMotionTokens,
    style?: CSSProperties,
): CSSProperties => ({
    opacity: visible ? 1 : 0,
    pointerEvents: visible ? 'auto' : 'none',
    transform: visible ? 'translateY(0)' : 'translateY(0.0625rem)',
    transition: [
        `opacity ${token.motionDurationFast} ${token.motionEaseInOut}`,
        `transform ${token.motionDurationFast} ${token.motionEaseInOut}`,
        `visibility ${token.motionDurationFast} ${token.motionEaseInOut}`,
    ].join(', '),
    visibility: visible ? 'visible' : 'hidden',
    ...style,
});

interface CollapsibleHoverRevealOptions {
    expandedMaxWidth?: CSSProperties['maxWidth'];
    gap?: CSSProperties['marginInlineStart'];
    side?: 'leading' | 'trailing';
}

interface CollapsibleBlockHoverRevealOptions {
    expandedMaxHeight?: CSSProperties['maxHeight'];
    gap?: CSSProperties['marginBlockStart'];
}

/**
 * Reveals a hover control without reserving idle layout space. This is intended
 * for inline labels and tabs where an invisible mounted action would otherwise
 * leave a visible gap.
 */
export const getCollapsibleHoverRevealStyle = (
    visible: boolean,
    token: HoverRevealMotionTokens,
    options: CollapsibleHoverRevealOptions = {},
    style?: CSSProperties,
): CSSProperties => {
    const baseStyle = getHoverRevealStyle(visible, token);
    const gapProperty = options.side === 'leading' ? 'marginInlineEnd' : 'marginInlineStart';

    return {
        ...baseStyle,
        display: 'inline-flex',
        flex: 'none',
        [gapProperty]: visible ? (options.gap ?? '0.375rem') : 0,
        maxWidth: visible ? (options.expandedMaxWidth ?? '1.5rem') : 0,
        overflow: 'hidden',
        transition: [
            baseStyle.transition,
            `max-width ${token.motionDurationFast} ${token.motionEaseInOut}`,
            `${gapProperty.replace(/[A-Z]/gu, (character) => `-${character.toLowerCase()}`)} ${token.motionDurationFast} ${token.motionEaseInOut}`,
        ]
            .filter(Boolean)
            .join(', '),
        ...style,
    };
};

/**
 * Vertical counterpart to `getCollapsibleHoverRevealStyle`. It reuses the
 * platform hover motion while removing all idle block-axis whitespace.
 */
export const getCollapsibleBlockHoverRevealStyle = (
    visible: boolean,
    token: HoverRevealMotionTokens,
    options: CollapsibleBlockHoverRevealOptions = {},
    style?: CSSProperties,
): CSSProperties => {
    const baseStyle = getHoverRevealStyle(visible, token);

    return {
        ...baseStyle,
        marginBlockStart: visible ? (options.gap ?? '0.375rem') : 0,
        maxHeight: visible ? (options.expandedMaxHeight ?? '4rem') : 0,
        overflow: 'hidden',
        transition: [
            baseStyle.transition,
            `max-height ${token.motionDurationFast} ${token.motionEaseInOut}`,
            `margin-block-start ${token.motionDurationFast} ${token.motionEaseInOut}`,
        ]
            .filter(Boolean)
            .join(', '),
        ...style,
    };
};
